import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useIntl, useLocation, useSelector, history, getLocale } from 'umi';
import { Guide } from '@/pages/home/components/Guide';
import {
  INTELLIGENCE_GUIDE_HIDE,
  INTELLIGENCE_GUIDE_BOX_LIST2,
} from '@/pages/home/components/GuideModule/GuideIntelligenceBox';
import withDelayWrapper from './DelayWrapper';
import {
  // GUIDE_SYS_INTELLIGENCE,
  GUIDE_TYPE_INTELLIGENCE_COMPANY,
} from '@/pages/home/models/guide';
import { storage } from '@/utils/utils';
import { FROM_PREFIX, DEFAULT_COMPANYID, parseUserGuideStep, generateURLByUserGuideStep } from './utils';
import { LANGUAGE_CN } from '@intelligence/_config/config';

import type { IGuideFrom } from './types';

import './index.scss';

import IMG_COMPANY_SELECT_EN from './imgs/en/company-select.png';
import IMG_COMPANY_SELECT_CN from './imgs/cn/company-select.png';

export { DEFAULT_COMPANYID as DEFAULT_GUIDE_COMPANYID };

export const COMPANY_GUIDE_CLASSNAMES = {
  ELEMENT1: 'card-item',
  ELEMENT2: 'dbi-user-guide__company-summary-card',
  ELEMENT2_HIGHLIGHT1: 'dbi-user-guide__menu-companies',
  ELEMENT2_HIGHLIGHT2: '.dbi-user-guide__top-companies__tables-card .dbi-mobileGames-table .tea-table__header thead tr th:nth-of-type(5)',
  ELEMENT3: 'dbi-user-guide__company-select',
  ELEMENT4: 'dbi-user-guide__company-games-share-title',
  ELEMENT4_HIGHLIGHT1: 'dbi-user-guide__company-details-tab-gamesstats',
};

const STEP1_PATH = '/search';
const STEP2_PATH = '/intelligence/topCharts/companies/Revenue';
const STEP3_PATH = `/intelligence/topCharts/companies/gamesDetails/${DEFAULT_COMPANYID}/Summary/revenue`;
const STEP4_PATH = `/intelligence/topCharts/companies/gamesDetails/${DEFAULT_COMPANYID}/GamesStats`;
const STEP_PATH_LIST = [STEP1_PATH, STEP2_PATH, STEP3_PATH, STEP4_PATH];
const USER_GUIDE_FROMS_CHARS = Array.from({ length: STEP_PATH_LIST.length - 1 }).map(
  (item, idx) => `${FROM_PREFIX}_${idx + 1}`,
);

const GuideCompanyContext = () => {
  const location: any = useLocation();
  const { pathname, query } = location;
  const { from } = query || {};
  const initialUserGuideStep = parseUserGuideStep(from);

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isCN = locale === LANGUAGE_CN;

  const dispatch = useDispatch();

  const { guideType, userInfo } = useSelector((state: any) => {
    const guideState = state?.guide;
    const homeState = state?.home;

    return {
      guideType: guideState?.guideType,
      userInfo: homeState?.userInfo,
    };
  });

  const userId = userInfo?.openid;

  const [currentGuideStep, setCurrentGuideStep] = useState(-1);

  const steps = useMemo<any[]>(
    () => [
      {
        element: () => document.querySelector(`.${COMPANY_GUIDE_CLASSNAMES.ELEMENT1}`),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 2 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_2_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_2_1_desc' }),
        placement: 'bottom',
        bubbleStyle: {},
      },
      {
        highlightElements: [
          {
            element: document.querySelector(`.${COMPANY_GUIDE_CLASSNAMES.ELEMENT2_HIGHLIGHT1}`),
          },
          {
            element: document.querySelector(COMPANY_GUIDE_CLASSNAMES.ELEMENT2_HIGHLIGHT2),
          },
        ],
        element: () => document.querySelector(`.${COMPANY_GUIDE_CLASSNAMES.ELEMENT2}`),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 2 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_2_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_2_2_desc' }),
        placement: 'top-start',
        bubbleStyle: {},
      },
      {
        element: () => document.querySelector(`.${COMPANY_GUIDE_CLASSNAMES.ELEMENT3}`),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 2 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_2_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_2_3_desc' }),
        arrowPointAtCenter: false,
        placement: 'left',
        placementOffset: [200, 10],
        bubbleStyle: {},
        extImage: {
          image: isCN ? IMG_COMPANY_SELECT_CN : IMG_COMPANY_SELECT_EN,
          placement: 'bottom-start',
          style: {
            marginLeft: -118,
            width: 230,
            height: 330,
          },
        },
      },
      {
        highlightElements: [
          {
            element: document.querySelector(`.dbi-topcharts-detail-fixedheader .${COMPANY_GUIDE_CLASSNAMES.ELEMENT4_HIGHLIGHT1}`),
            padding: {
              paddingLeft: 10,
              paddingRight: 10,
            },
          },
        ],
        element: () => document.querySelector(`.${COMPANY_GUIDE_CLASSNAMES.ELEMENT4}`),
        padding: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        hasRadius: false,
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 2 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_2_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_2_4_desc' }),
        autoScrollIntoView: false,
        placement: 'bottom',
        bubbleStyle: {},
      },
    ],
    [locale],
  );

  const handleFinished = () => {
    if (userId) {
      let list = storage.getLocalStorageItem(INTELLIGENCE_GUIDE_BOX_LIST2);
      list = Array.isArray(list) ? list : [];
      if (!list.includes(userId)) {
        list.push(userId);
      }
      storage.setLocalStorageItem(INTELLIGENCE_GUIDE_BOX_LIST2, list);
    }
    dispatch({
      type: 'guide/showIntelligenceGuideModule',
      payload: true,
    });
  };

  // const handleCloseStep = () => {
  //   dispatch({
  //     type: 'guide/save',
  //     payload: {
  //       guideSys: GUIDE_SYS_INTELLIGENCE,
  //       guideType: '',
  //     },
  //   });
  // };

  const handleBack = (current: number) => {
    const prevPath = STEP_PATH_LIST[current];
    if (prevPath === pathname) {
      const path = generateURLByUserGuideStep(prevPath, current);
      history.replace(path);
      return;
    }
    if (prevPath === STEP3_PATH && pathname === STEP4_PATH) {
      const path = generateURLByUserGuideStep(STEP3_PATH, current);
      history.replace(path);
      return;
    }
    history.goBack();
  };

  const handleNext = (current: number) => {
    if (current >= 0) {
      const { pathname } = location;
      const nextPathname = STEP_PATH_LIST[current];
      const routeAction =
        pathname === nextPathname || (pathname === STEP3_PATH && nextPathname === STEP4_PATH) ? 'replace' : 'push';
      const routePath = generateURLByUserGuideStep(nextPathname, current);
      history[routeAction](routePath);
    }
  };

  const handleCurrentChange = (current: number, context: { from: IGuideFrom }) => {
    const { from } = context || {};
    setCurrentGuideStep(current);
    if (from === 'back') {
      handleBack(current);
    } else if (from === 'next') {
      handleNext(current);
    } else if (from === 'finish') {
      handleFinished();
    } else if (from === 'cancel') {
      // handleCloseStep();
      handleFinished();
    }
  };

  // 注意下面两个useEffect不要合并到一起，不然会出现：从头部导航进入新手引导，点击到下一步时，关闭按钮无法关闭，而是返回到第一步
  useEffect(() => {
    if (guideType !== GUIDE_TYPE_INTELLIGENCE_COMPANY) return;
    if (pathname !== STEP1_PATH && !USER_GUIDE_FROMS_CHARS.includes(from)) return;
    setCurrentGuideStep(initialUserGuideStep);
  }, [guideType, from, pathname, initialUserGuideStep]);

  useEffect(() => {
    const list = storage.getLocalStorageItem(INTELLIGENCE_GUIDE_BOX_LIST2);
    const guideHide = storage.getLocalStorageItem(INTELLIGENCE_GUIDE_HIDE);
    if (!userId || list?.includes(userId) || guideHide) return;
    if (pathname !== STEP1_PATH && !USER_GUIDE_FROMS_CHARS.includes(from)) return;

    setCurrentGuideStep(initialUserGuideStep);
  }, [userId, from, pathname, initialUserGuideStep]);

  return (
    // finishText 根据中英文调整
    <Guide
      visibleCloseBtn
      hideCancelButton
      showBackButton
      nextButtonTheme="primary"
      backText={formatMessage({ id: 'guide_last_step' })}
      nextText={formatMessage({ id: 'guide_next_step' })}
      finishText={formatMessage({ id: 'guide_more_step' })}
      steps={steps}
      current={currentGuideStep}
      onCurrentChange={handleCurrentChange}
    />
  );
};

const GuideCompany = () => {
  const location = useLocation();

  const { guideType } = useSelector((state: any) => {
    const guideState = state?.guide;
    return {
      guideType: guideState?.guideType,
    };
  });

  if (guideType !== GUIDE_TYPE_INTELLIGENCE_COMPANY) return null;
  if (!STEP_PATH_LIST.includes(location.pathname)) return null;

  return <GuideCompanyContext />;
};

export const GuideCompanyDelay = withDelayWrapper(GuideCompany);

export default GuideCompany;
